import React, { FC, useState } from 'react';
import PostRequirement from 'components/posts/detail/overview/rightSection/infoCard/postRequirements/PostRequirement';
import PostRequirementsView from 'components/shared/detail/postRequirementsView/PostRequirementsView';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import {
  usePostOverviewOptimisticQueryUpdater,
  usePostOverviewQuery,
} from 'app/modules/posts/modules/post/hooks/usePostOverview';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import NewPostRequirementModal from 'app/components/modals/newPostRequirement/NewPostRequirementModal';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { Button, Chip, Icon, TextLink } from '@kontentino/ui';
import { stopPropagation } from 'utils/mouseEvent';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useTranslation } from 'react-i18next';
import useBoolean from 'utils/hooks/useBoolean';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';

type Props = {
  id: number;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const PostRequirements: FC<Props> = (props) => {
  const { data, isLoading, isError } = usePostOverviewQuery(props.id);
  const { invalidateData } = usePostOverviewOptimisticQueryUpdater(props.id);
  const [modalOpen, setModalOpen] = useState(false);
  const [hideCompletedRequirements, setHideCompletedRequirements] =
    useBoolean(false);
  const { t } = useTranslation();
  const completedRequirementsCount =
    data?.requirements.filter(({ checked }) => checked).length ?? 0;

  return (
    <>
      <AccordionWidget
        data-name="posts-preview_requirements"
        icon={<AccordionWidgetIcon variant="postRequirements" />}
        title={<AccordionWidgetTitle variant="postRequirements" />}
        titleSuffix={(isOpen) =>
          !!data?.requirements?.length && (
            <Chip variant="darkGrey">
              <Icon icon={faCheck} size="sm" />
              {completedRequirementsCount} / {data?.requirements.length}
            </Chip>
          )
        }
        {...props.accordionProps}
      >
        <PostRequirementsView
          requirements={data?.requirements ?? []}
          isLoading={isLoading}
          isError={isError}
        >
          {(requirements) =>
            requirements
              .filter(({ checked }) => !hideCompletedRequirements || !checked)
              .map((requirement) => (
                <PostRequirement
                  requirement={requirement}
                  postId={props.id}
                  key={requirement.id}
                />
              ))
          }
        </PostRequirementsView>

        <div className="tw-mt-3 tw-flex tw-justify-between">
          {data && (
            <UserPermissionGate
              scopes={[UserPermission.POST_CREATE_REQUIREMENTS]}
            >
              <TextLink>
                <Button
                  onClick={stopPropagation(() => setModalOpen(true))}
                  variant="plain"
                  iconBefore={<Icon icon={faPlus} />}
                >
                  {t('addNew')}
                </Button>
              </TextLink>
            </UserPermissionGate>
          )}
          {!!data?.requirements.length && (
            <div className="tw-ml-auto">
              <span className="tw-mr-1 tw-text-sm tw-font-medium tw-text-grayscale-100">
                {t('completedRequirements', {
                  completed: completedRequirementsCount,
                  total: data?.requirements.length,
                })}{' '}
                /
              </span>
              <TextLink>
                <Button
                  variant="plain"
                  size="small"
                  onClick={setHideCompletedRequirements.toggle}
                >
                  {hideCompletedRequirements ? t('show') : t('hide')}
                </Button>
              </TextLink>
            </div>
          )}
        </div>
      </AccordionWidget>

      {data?.page && (
        <UserPermissionGate scopes={[UserPermission.POST_CREATE_REQUIREMENTS]}>
          <NewPostRequirementModal
            open={modalOpen}
            onHide={() => {
              setModalOpen(false);
              invalidateData();
            }}
            page={data.page}
          />
        </UserPermissionGate>
      )}
    </>
  );
};

export default PostRequirements;
